import {Component, HostListener, OnInit} from '@angular/core';
import {Device} from '../../model/device';
import {DevicesService} from '../../services/devices.service';
import {DeviceFilters} from '../../model/filters/device-filters';
import {Organization} from '../../model/organization';
import {OrganizationsService} from '../../services/organizations.service';
import {SortOrder} from '../../model/sort-order';
import {DeviceCategoriesService} from '../../services/device-categories.service';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {CommonMessagesService} from '../../services/common-messages.service';
import {TenantsService} from '../../services/tenants.service';
import {Tenant} from '../../model/tenant';
import {AuthGuard} from '../../common/auth-guard.service';
import {LoggedUser} from '../../common/logged-user.service';
import {TimezoneService} from '../../services/timezone.service';
import * as moment from 'moment';
import {PreviousRouteService} from '../../services/previous-route.service';
import {FiltersService} from "../../services/filters.service";
import {SharedResource} from "../../model/shared-resource";
import {DecodersService} from "../../services/decoders.service";
import {Decoder} from "../../model/decoder";

@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss']
})
export class DevicesComponent implements OnInit {

  public devices: Device[];
  public totalDevices: number;
  public loadingDevices: boolean;
  public typeExport: 'XLSX' | 'CSV';
  public userData: any;

  public rows = 10;
  public first = 0;
  public selected = [];

  public filtersVisible: boolean;
  public currentFilters: DeviceFilters;
  public dateRanges: Date[];
  public minLastValueDate: Date;
  public categories: MenuItem[];
  public tenants: Tenant[];
  public organizations: Organization[];
  public decoders: Decoder[];
  public filtersFilled: boolean;

  public sharedResources: Array<SharedResource> = [];
  public showDialog: boolean = false;
  public exportVisible: boolean;

  constructor(private devicesService: DevicesService,
              private deviceCategoriesService: DeviceCategoriesService,
              private organizationsService: OrganizationsService,
              private messageService: MessageService,
              private i18n: TranslocoService,
              private confirmationService: ConfirmationService,
              private commonMessagesService: CommonMessagesService,
              private tenantsService: TenantsService,
              private timezoneService: TimezoneService,
              public loggedUser: LoggedUser,
              public authGuardService: AuthGuard,
              private previousService: PreviousRouteService,
              private filtersService: FiltersService<DeviceFilters>,
              private decodersService: DecodersService) {
    this.currentFilters = new DeviceFilters();
    loggedUser.userObservable.subscribe(user => {
      this.userData = user;
    });
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    this.loadFilters().then(() => {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
      if (this.currentFilters?.hasOwnProperty('rangeDates')) {
        this.dateRanges = [new Date(this.currentFilters.rangeDates[0]), new Date(this.currentFilters.rangeDates[1])];
      }
      if (this.currentFilters?.hasOwnProperty('minLastValueDate')) {
        this.minLastValueDate = new Date(this.currentFilters.minLastValueDate);
      }
    });
    this.devices = new Array(this.rows);
  }

  private loadDevices(offset: number, limit: number): void {
    this.loadingDevices = true;
    this.devices = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.formatterDateForm(this.dateRanges);
    this.formatterMinLastValueDate(this.minLastValueDate);

    this.devicesService.getDevices(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters : this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        this.devices = response.devices;
        this.totalDevices = response.totalElements;
        this.loadingDevices = false;
      });
  }

  private loadFilters(): Promise<any> {
    let promises: Array<Promise<any>> = [this.deviceCategoriesService.getDeviceCategories().toPromise(), this.organizationsService.getOrganizations().toPromise(),
        this.decodersService.getDecoders().toPromise()];
    if (this.authGuardService.superAdminRequired()) {
      promises.push(this.tenantsService.getTenants().toPromise());
    }
    return Promise.all(promises).then(response => {
      this.categories = response[0].deviceCategories
        .map(category => category.name)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map(categoryName => {
          return {
            label: categoryName,
            value: categoryName
          };
        });

      this.organizations = response[1].organizations.sort((a, b) => a.name > b.name ? 1 : -1);

      this.decoders = response[2].decoders
        .map(decoder => decoder.name)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map(decoderName => {
          return {
            label: decoderName,
            value: decoderName
          };
        });

      if (this.authGuardService.superAdminRequired()) {
        this.tenants = response[3].tenants.sort((a, b) => a.name > b.name ? 1 : -1);
      }
    });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadDevices(event.first / event.rows, event.rows);
  }

  resetFilters(): void {
    this.currentFilters = new DeviceFilters();
    this.currentFilters.sortField = 'dateLastValueReported';
    this.currentFilters.sortOrder = 0;
    this.dateRanges = null;
    this.minLastValueDate = null;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadDevices(this.first / this.rows, this.rows);
  }

  confirmAndDelete(device: Device): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteDevice(device.uuid);
    });
  }

  showSharedDialog(sharedResource: Device): void {
    this.sharedResources = [new SharedResource('DEVICE', sharedResource.serialNumber, sharedResource.uuid,
      sharedResource.tenant.uuid, sharedResource.organization.uuid)];
    this.showDialog = true;
  }

  deleteDevice(uuid: string): void {
    this.devicesService.deleteDevice(uuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadDevices(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) {
      return;
    }
    const uuids = this.selected.map(item => item.uuid);

    this.devicesService.deleteDevices(uuids).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadDevices(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  trimFieldsForm(filters: DeviceFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  formatterDateForm(rangeDates: Date[]): void {
    if (rangeDates && rangeDates.length > 1) {
      if (!rangeDates[1]) {
        rangeDates[1] = moment.utc().toDate();
      }
      const utcRange = this.timezoneService.timestampRangeFromCalendarData(rangeDates);
      this.currentFilters.rangeDates = [utcRange.from, utcRange.until];
    }
  }

  formatterMinLastValueDate(date: Date): void {
    if (date) {
      this.currentFilters.minLastValueDate = this.minLastValueDate.getTime();
    }
  }

  get filteredOrganizations(): Organization[] {
    if (this.authGuardService.superAdminRequired()) {
      if (this.currentFilters.tenantUuids?.length > 0) {
        return this.organizations?.filter(org => this.currentFilters.tenantUuids.includes(org.tenant.uuid));
      }
    }
    return this.organizations;
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f' && !this.showDialog) {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
